import { IAccommodationBlockingForm } from './accommodation-blocking-form';
import {
  IAccommodationBlockingLookups,
  IAccommodationBlockingPayload,
  IAccommodationBlockingPayloadResult,
} from './accommodation-blocking-payload';
import { IAccommodationBlocking } from '../../../store/actions/sales-configuration';
import { toApiDate, makeDate } from '../../../utilities/formatting';
import { OPEN_DATE_TO, isOpenDate } from '../accommodation-blocking-date-range/accommodation-blocking-date-range';

const SALES_STEERING_TYPE_MAP: Record<IAccommodationBlockingForm['blockingType'], 'CheckIn' | 'CheckOut' | 'Stay'> = {
  checkIn: 'CheckIn',
  checkOut: 'CheckOut',
  stay: 'Stay',
};

// eslint-disable-next-line max-len
const SALES_STEERING_TYPE_REVERSE_MAP: Record<'CheckIn' | 'CheckOut' | 'Stay', IAccommodationBlockingForm['blockingType']> = {
  CheckIn: 'checkIn',
  CheckOut: 'checkOut',
  Stay: 'stay',
};

const TRANSPORT_DIRECTION_MAP: Record<'outbound' | 'inbound', 'Outbound' | 'Inbound'> = {
  outbound: 'Outbound',
  inbound: 'Inbound',
};

const TRANSPORT_DIRECTION_REVERSE_MAP: Record<'Outbound' | 'Inbound', 'outbound' | 'inbound'> = {
  Outbound: 'outbound',
  Inbound: 'inbound',
};

// Resolves form codes to ids. Any code without a matching entity in the store is reported
// via `unresolved` rather than silently dropped, so the caller can surface it as a violation
// instead of sending an incomplete payload to the API.
function mapCodesToIds<T extends { id: number }>(
  codes: string[],
  entities: T[],
  getCode: (entity: T) => string,
  unresolved: string[],
  label: string
): number[] {
  const ids: number[] = [];
  for (const code of codes) {
    const entity = entities.find((item) => getCode(item) === code);
    if (entity) {
      ids.push(entity.id);
    } else {
      unresolved.push(`${label} "${code}" could not be resolved.`);
    }
  }
  return ids;
}

export interface IExistingAccommodationBlocking {
  id: number;
  accommodationBlockingKey: string;
  createdBy: string;
  createdDate: string;
}

// Builds the POST payload and resolves form codes to ids. When any code cannot be resolved
// against the given lookups, `unresolved` carries a violation message per code; callers should
// treat a non-empty `unresolved` list as blocking and not submit `payload` to the API.
//
// `existing` distinguishes create from update (see docs/adr/0009): when present, its `id` /
// `createdBy` / `createdDate` are preserved on the payload and `modifiedBy`/`modifiedDate` are
// set to the current user/now instead; when absent, the payload is shaped for a brand new
// record (`id: 0`, `createdBy`/`createdDate` set from the current user/now, `modifiedBy`/
// `modifiedDate` left `null`).
export function buildAccommodationBlockingPayload(
  form: IAccommodationBlockingForm,
  lookups: IAccommodationBlockingLookups,
  currentUser: string,
  existing?: IExistingAccommodationBlocking
): IAccommodationBlockingPayloadResult {
  const {
    publications,
    countries,
    regions,
    destinations,
    accommodations,
    airportsOrBusStops,
    dateRange,
    direction,
    blockingType,
    remark,
    durations,
  } = form;

  const unresolved: string[] = [];
  const publicationIds = mapCodesToIds(
    publications,
    lookups.publications,
    (publication) => publication.publicationCode,
    unresolved,
    'Publication'
  );
  const accommodationIds = mapCodesToIds(
    accommodations,
    lookups.accommodations,
    (accommodation) => accommodation.code,
    unresolved,
    'Accommodation'
  );
  const transportHubIds = mapCodesToIds(
    airportsOrBusStops,
    lookups.transportHubs,
    (transportHub) => transportHub.code,
    unresolved,
    'Airport / Bus Stop'
  );

  const now = new Date().toISOString();

  const payload: IAccommodationBlockingPayload = {
    id: existing?.id ?? 0,
    accommodationBlockingKey: existing?.accommodationBlockingKey ?? crypto.randomUUID(),
    publicationIds,
    countryCodes: countries,
    regionCodes: regions,
    destinationCodes: destinations,
    accommodationIds,
    transportHubIds,
    startDate: toApiDate(dateRange.dateFrom),
    endDate: toApiDate(dateRange.dateTo),
    // eslint-disable-next-line unicorn/no-null
    transportDirection: direction ? TRANSPORT_DIRECTION_MAP[direction] : null,
    salesSteeringType: SALES_STEERING_TYPE_MAP[blockingType],
    remark,
    // eslint-disable-next-line unicorn/no-null
    durations: durations.trim() || null,
    createdBy: existing?.createdBy ?? currentUser,
    createdDate: existing?.createdDate ?? now,
    // eslint-disable-next-line unicorn/no-null
    modifiedBy: existing ? currentUser : null,
    // eslint-disable-next-line unicorn/no-null
    modifiedDate: existing ? now : null,
  };

  return { payload, unresolved };
}

// Reverse of buildAccommodationBlockingPayload: seeds the "New" form's state from a fetched
// IAccommodationBlocking record (TD-10655 Edit). Publications/Accommodations/Transport Hubs are
// already resolved to {id, code} pairs server-side (see docs/adr/0004); only their `code` is
// needed here since the dropdowns are keyed by code.
export function mapAccommodationBlockingToForm(blocking: IAccommodationBlocking): IAccommodationBlockingForm {
  return {
    publications: blocking.publications.map((publication) => publication.code),
    countries: blocking.countryCodes,
    regions: blocking.regionCodes,
    destinations: blocking.destinationCodes,
    accommodations: blocking.accommodations.map((accommodation) => accommodation.code),
    airportsOrBusStops: blocking.transportHubs.map((transportHub) => transportHub.code),
    dateRange: {
      dateFrom: makeDate(blocking.startDate) as Date,
      dateTo: isOpenDate(blocking.endDate) ? OPEN_DATE_TO : (makeDate(blocking.endDate) as Date),
      openDate: isOpenDate(blocking.endDate),
    },
    // eslint-disable-next-line unicorn/no-null
    direction: blocking.transportDirection ? TRANSPORT_DIRECTION_REVERSE_MAP[blocking.transportDirection] : null,
    blockingType: SALES_STEERING_TYPE_REVERSE_MAP[blocking.salesSteeringType],
    remark: blocking.remark,
    durations: blocking.durations ?? '',
  };
}
